// The `evidence/source` transcript-chips api module — carved from
// `src/lib/api/transcript-chips.ts` (now a pure re-export delegate).
//
// DOMAIN RULING. All six fns are `/transcript-chips/*` routes: the in-session
// suggestion generator, the auto-evidence config read/write, and the
// session-restoration trio. Judged by the EN1 resource rule — the test that put
// CPD (`/cpd/*`) out of this lane and `useBlockedDomains` out
// of it on `/chat/*` — that is one route family with one owner, so the module
// carves whole rather than being split by which app surface calls which fn. The
// pre-move file was tagged `@domain REVIEW` per-endpoint because it sat inside
// the `src/lib/api/**` hub globs; the tags do not travel, because the hub census
// does not glob `packages/**` and a re-export delegate yields no rows.
//
// S1, the transport severance, is the whole mechanical content of this move:
// `@/utils/scribeHTTP`'s module singleton becomes
// `getScribeHttpClients().scribeHttpV2` from the `@heidi/api/client` registry.
// The registry hands back
// that SAME instance — same interceptors, same auth/refresh state — so paths,
// verbs, bodies and `.data` unwrapping are byte-identical to the pre-move module.
//
// The client is resolved PER CALL through `http()` rather than destructured at
// module scope. That is not a style choice: the registry is populated by
// `src/utils/scribeHTTP.ts` at factory time, and a module-scope read here would
// resolve at import time — i.e. whenever a bundler decides to evaluate this
// chunk, which for `_app`-reachable code can be before bootstrap has run.
// `data/api/sourceLists.ts` takes the same shape for the same reason.
//
// `ScribeHttp` keeps the signatures structural, so no axios type reaches this
// package's surface and `axios` stays out of its manifest.
//
// The basename is KEPT (`transcript-chips.ts`, not `transcriptChips.ts`): a
// moved file keeps its basename, and unlike `sourceLists.ts` — whose source was
// called `api.ts` and would have landed as `data/api/api.ts` — there is nothing
// here forcing a rename.
import { getScribeHttpClients } from '@heidi/api/client';

import type {
  AutoEvidenceConfig,
  AutoEvidenceConfigUpdate,
  LinkChipToChatRequest,
  SessionChipsResponse,
  TranscriptChipsRequest,
  TranscriptChipsResponse,
} from '../../contracts';

interface ScribeHttp {
  get<T>(url: string): Promise<{ data: T }>;
  post<T>(url: string, body?: unknown): Promise<{ data: T }>;
  put<T>(url: string, body?: unknown): Promise<{ data: T }>;
  delete<T>(url: string): Promise<{ data: T }>;
}

// `scribeHttpV2` base is /api/v2/ml-scribe/; scribe JWT injected by interceptor.
const http = (): ScribeHttp => getScribeHttpClients().scribeHttpV2;

/**
 * Generate suggested Evidence queries from transcript.
 */
export async function suggestChips(
  request: TranscriptChipsRequest
): Promise<TranscriptChipsResponse> {
  const { data } = await http().post<TranscriptChipsResponse>(
    '/transcript-chips/suggest',
    request
  );
  return data;
}

/**
 * Get the user's Auto-Evidence configuration.
 */
export async function getConfig(): Promise<AutoEvidenceConfig> {
  const { data } = await http().get<AutoEvidenceConfig>('/transcript-chips/config');
  return data;
}

/**
 * Update the user's Auto-Evidence configuration.
 */
export async function updateConfig(
  request: AutoEvidenceConfigUpdate
): Promise<AutoEvidenceConfig> {
  const { data } = await http().put<AutoEvidenceConfig>(
    '/transcript-chips/config',
    request
  );
  return data;
}

/**
 * Get session chips and chat mappings for state restoration.
 */
export async function getSessionChips(sessionId: string): Promise<SessionChipsResponse> {
  const { data } = await http().get<SessionChipsResponse>(
    `/transcript-chips/session/${sessionId}`
  );
  return data;
}

/**
 * Link a chip to an Evidence chat for answer lookup.
 */
export async function linkChipToChat(request: LinkChipToChatRequest): Promise<void> {
  await http().post('/transcript-chips/link-chat', request);
}

/**
 * Clear transcript chips context for a session.
 */
export async function clearSessionContext(sessionId: string): Promise<void> {
  await http().delete(`/transcript-chips/session/${sessionId}`);
}

/**
 * The namespace object every consumer actually imports.
 *
 * Kept — rather than repointing the ~6 call sites at the named exports — because
 * it is what `jest.mock('@/lib/api/transcript-chips', () => ({ transcriptChipsApi: … }))`
 * names in three app suites. Losing it would turn a behaviour-preserving move
 * into a call-site migration across the in-session surface.
 */
export const transcriptChipsApi = {
  suggestChips,
  getConfig,
  updateConfig,
  getSessionChips,
  linkChipToChat,
  clearSessionContext,
};
